import VoxelStatistics from '../enums/VoxelStatistics.js';
import { getBufferConfiguration } from '../utilities/getBufferConfiguration.js';
import { maxEdgeOfGrid, voxelCountOfGrid } from '../utilities/voxelGrid/index.js';
import { MutableVolumeTextureSlab, VolumeTextureSet, } from './classes/VolumeTextureSet.js';
import { coreLog } from '../utilities/logger.js';
import eventTarget from '../eventTarget.js';
import Events from '../enums/Events.js';
const log = coreLog.getLogger('cache', 'volumeTextureStore');
class VolumeTextureStore {
    constructor() {
        this.sets = new Map();
        this.budgetBytes = 0;
        this.clock = 0;
    }
    get bytesUsed() {
        let total = 0;
        for (const stored of this.sets.values()) {
            total += stored.set.bytes;
        }
        return total;
    }
    get budget() {
        return this.budgetBytes;
    }
    setBudget(bytes) {
        this.budgetBytes = bytes > 0 ? bytes : 0;
    }
    get bytesAvailable() {
        return this.budgetBytes > 0
            ? Math.max(this.budgetBytes - this.bytesUsed, 0)
            : Number.POSITIVE_INFINITY;
    }
    setsOfVolume(volumeId) {
        const found = [];
        for (const stored of this.sets.values()) {
            if (stored.volumeId === volumeId) {
                found.push(stored.set);
            }
        }
        return found;
    }
    getSet(volumeId, name) {
        return this.sets.get(keyOf(volumeId, name))?.set;
    }
    costOf(grid, dataType, numberOfComponents = 1) {
        const voxels = voxelCountOfGrid(grid) * Math.max(numberOfComponents, 1);
        if (!dataType) {
            return voxels;
        }
        return getBufferConfiguration(dataType, voxels, { isVolumeBuffer: true })
            .numBytes;
    }
    provision(options) {
        const { volumeId, name, volumeGrid, grids, dataType, numberOfComponents = 1, statistic = VoxelStatistics.Average, coverage, backstop, ownerId, createTexture, destroyTexture, applyGrid, limits, } = options;
        const key = keyOf(volumeId, name);
        const existing = this.sets.get(key);
        if (existing) {
            existing.set.lastUsed = ++this.clock;
            return existing.set;
        }
        if (grids.length === 0) {
            return undefined;
        }
        for (const grid of grids) {
            if (!withinLimits(grid, limits)) {
                log.warn(`the set ${name} of the volume ${volumeId} holds a grid of ${grid.dimensions} that exceeds the limits, so nothing is built`);
                return undefined;
            }
        }
        const perTexture = grids.map((grid) => this.costOf(grid, dataType, numberOfComponents));
        const bytes = perTexture.reduce((total, cost) => total + (ownerId ? cost * 2 : cost), 0);
        if (!this.makeRoomFor(bytes)) {
            log.warn(`the budget of ${this.budgetBytes} bytes cannot hold the set ${name} of the volume ${volumeId}, which costs ${bytes} bytes`);
            return undefined;
        }
        const set = new VolumeTextureSet({
            name,
            volumeGrid,
            statistic,
            coverage: coverage ?? (grids.length === 1 ? 'full-extent' : 'partial'),
            spacing: grids[0].spacing,
            backstop,
        });
        grids.forEach((grid, index) => {
            const id = `${name}#${index}`;
            if (ownerId) {
                set.add(new MutableVolumeTextureSlab({
                    id,
                    ownerId,
                    grid,
                    statistic,
                    bytes: perTexture[index],
                    front: createTexture({ grid, statistic, setName: name, id }),
                    back: createTexture({ grid, statistic, setName: name, id }),
                    applyGrid: applyGrid ?? (() => undefined),
                }));
                return;
            }
            const texture = createTexture({ grid, statistic, setName: name, id });
            applyGrid?.(texture, grid);
            const slot = {
                mutability: 'fixed',
                id,
                grid,
                statistic,
                texture,
                bytes: perTexture[index],
                dirty: [],
            };
            set.add(slot);
        });
        set.markAllDirty(options.markTextureSlice);
        set.lastUsed = ++this.clock;
        this.sets.set(key, {
            volumeId,
            set: set,
            destroyTexture: destroyTexture,
        });
        return set;
    }
    claim(volumeId, name) {
        const stored = this.sets.get(keyOf(volumeId, name));
        if (!stored) {
            return false;
        }
        stored.set.references++;
        stored.set.lastUsed = ++this.clock;
        return true;
    }
    pin(volumeId, name) {
        const stored = this.sets.get(keyOf(volumeId, name));
        if (!stored) {
            return false;
        }
        stored.set.pinned = true;
        stored.set.lastUsed = ++this.clock;
        return true;
    }
    release(volumeId, name) {
        const stored = this.sets.get(keyOf(volumeId, name));
        if (!stored) {
            return false;
        }
        stored.set.references = Math.max(stored.set.references - 1, 0);
        return true;
    }
    evict(volumeId, name) {
        const key = keyOf(volumeId, name);
        const stored = this.sets.get(key);
        if (!stored) {
            return false;
        }
        this.sets.delete(key);
        if (stored.destroyTexture) {
            for (const slot of stored.set.members()) {
                releaseTexturesOf(slot, stored.destroyTexture);
            }
        }
        return true;
    }
    evictVolume(volumeId) {
        for (const [key, stored] of [...this.sets.entries()]) {
            if (stored.volumeId === volumeId) {
                this.evict(stored.volumeId, key.slice(stored.volumeId.length + 1));
            }
        }
    }
    clear() {
        for (const [, stored] of [...this.sets.entries()]) {
            this.evictVolume(stored.volumeId);
        }
    }
    makeRoomFor(bytes) {
        if (this.budgetBytes <= 0 || this.bytesUsed + bytes <= this.budgetBytes) {
            return true;
        }
        const evictable = [...this.sets.values()]
            .filter((stored) => stored.set.references === 0 && !stored.set.pinned)
            .sort((a, b) => {
            if (a.set.backstop !== b.set.backstop) {
                return a.set.backstop ? 1 : -1;
            }
            return a.set.lastUsed - b.set.lastUsed;
        });
        for (const stored of evictable) {
            this.evict(stored.volumeId, stored.set.name);
            if (this.bytesUsed + bytes <= this.budgetBytes) {
                return true;
            }
        }
        return false;
    }
}
function keyOf(volumeId, name) {
    return `${volumeId}|${name}`;
}
function withinLimits(grid, limits) {
    const { maxEdge, maxVoxelCount } = limits ?? {};
    if (maxEdge > 0 && maxEdgeOfGrid(grid) > maxEdge) {
        return false;
    }
    if (maxVoxelCount > 0 && voxelCountOfGrid(grid) > maxVoxelCount) {
        return false;
    }
    return true;
}
function releaseTexturesOf(slot, destroyTexture) {
    if (slot.mutability === 'owned') {
        const slab = slot;
        const front = slab.beginRender();
        const back = slab.writeTarget();
        slab.endRender();
        destroyTexture(front);
        if (back !== front) {
            destroyTexture(back);
        }
        return;
    }
    destroyTexture(slot.texture);
}
const volumeTextureStore = new VolumeTextureStore();
eventTarget.addEventListener(Events.VOLUME_CACHE_VOLUME_REMOVED, (evt) => {
    const { volumeId } = evt.detail ?? {};
    if (volumeId) {
        volumeTextureStore.evictVolume(volumeId);
    }
});
export { VolumeTextureStore, volumeTextureStore };
export default volumeTextureStore;
